<template>
  <div class="__UsageReportAccount-Analyze">
    <HeroChart
      ref="hero-chart"
      name="usage-account-chart"
      :data="chartData"
      :date-range="{ start: start, end: end }"
      :granularity="granularity"
      :categories="chartCategories"
      :conditions="conditions"
      :min-date="minDate"
      :max-date="maxDate"
      action-menu
      data-table
      selectable-category
      :currency="currency"
      @update-date-range="onUpdateDateRange"
      @change-granularity="onUpdateGranularity"
      @change-category="onUpdateCategory"
      @change-setting-success="onChangeSettingSuccess({ owner, ownerId })"
      :loading="loading"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import heroChartMixin from '@/components/CustomCharts/heroChartMixin';
import CPanel from '@/components/uikit/CPanel';
import HeroChart from '@/components/CustomCharts/HeroChart';
import { mapGetters, mapState } from 'vuex';

export default Vue.extend({
  name: 'Analyze',
  mixins: [heroChartMixin],
  components: { CPanel, HeroChart },

  data() {
    return {
      isInitialized: false,
    };
  },

  computed: {
    ...mapGetters('usageReports', {
      owner: 'getOwner',
    }),
    ...mapState({
      dataType: state => state.userState.dataType,
      vendor: state => state.vendor,
    }),

    chartCategories() {
      const resource = this.$store.state.reports_v2.resource;
      const isCrossTagOrTag = resource === 'cross_tag' || resource === 'tag';

      if (isCrossTagOrTag) {
        return this.owner === 'company' ? ['service', 'resource'] : ['service', 'account'];
      }

      if (resource === 'account' || resource === 'group' || resource === 'azure_subscription') {
        switch (this.vendor) {
          case 'aws':
            return ['productCode', 'service', 'account', 'region', 'instanceType', 'tags'];
          case 'azure':
            return ['service', 'account'];
          case 'gcp':
            return ['service', 'account'];
          default:
            return ['service', 'account'];
        }
      }

      return ['productCode', 'service', 'account', 'region', 'instanceType', 'tags'];
    },

    chartName() {
      const isCrossTagOrTag =
        this.$store.state.reports_v2.resource === 'cross_tag' ||
        this.$store.state.reports_v2.resource === 'tag';
      if (isCrossTagOrTag) {
        return this.owner === 'company'
          ? 'crosstag-dashboard-graph'
          : 'crosstag-usage-report-graph';
      }
      return 'usage-account-chart';
    },
  },

  watch: {
    chartData: {
      handler(newData) {
        if (newData && newData.length && !this.isInitialized) {
          this.$nextTick(() => {
            this.initializeChart();
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    async initializeChart() {
      if (!this.$refs['hero-chart']) return;

      try {
        await this.$store.dispatch('reports_v2/resetTagState');

        const vendor = this.$store.state.vendor;
        const defaultCategory = vendor === 'aws' ? 'productCode' : 'service';

        // If current category is tags, ensure we have a tag key selected
        const currentCategory = this.$store.state.reports_v2.category;
        if (currentCategory === 'tags') {
          const heroChart = this.$refs['hero-chart'];
          if (heroChart.hasValidTagData()) {
            await heroChart.setInitialTagKey();
          } else {
            // Fallback to default category if no tag data
            await this.updateCondition({
              key: 'category',
              value: defaultCategory,
            });
          }
        } else {
          await this.updateCondition({
            key: 'category',
            value: defaultCategory,
          });
        }

        await this.$refs['hero-chart'].initialGenerateChart();
        this.isInitialized = true;
      } catch (error) {
        console.error('Failed to initialize chart:', error);
      }
    },

    generateChart() {
      return this.$refs['hero-chart']?.generateChart();
    },

    setSeries() {
      this.$refs['hero-chart']?.setInitialSeries();
    },

    // Ensure proper cleanup
    beforeDestroy() {
      this.isInitialized = false;
    },
  },

  async created() {
    const vendor = this.$store.state.vendor;
    const defaultCategory = vendor === 'aws' ? 'productCode' : 'service';

    await this.updateCondition({
      key: 'category',
      value: defaultCategory,
    });
  },

  async mounted() {
    if (this.chartData && this.chartData.length) {
      await this.initializeChart();
    }
  },
});
</script>

<style lang="scss" scoped></style>
